'use client';

import * as RadioGroup from '@radix-ui/react-radio-group';

import type {
  AttributesEntity,
  ValuesEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import type {
  CategoryFilterState,
  CategoryFilterUpdate,
} from '@eliadmin/core/src/components/categories/category-query-state';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';

import { byPosition, CheckList } from './filter-group';
import { formatNumber, parseNumber, RangeFilter } from './range-filter';

/** Types the core filter renders; image and range attributes have no filter control there. */
const FILTER_TYPES: string[] = [
  AttributeType.CHECKBOX,
  AttributeType.SIZE,
  AttributeType.RADIO,
  AttributeType.COLOR,
  AttributeType.MULTICOLOR,
  AttributeType.SELECT,
  AttributeType.SLIDER_RADIO,
  AttributeType.SLIDER_SELECT,
];

/** The core rule for attribute groups in the filter, without the empty image/range groups. */
export const isFilterAttribute = (attribute: AttributesEntity) =>
  !!attribute.name && !!attribute.values?.length && FILTER_TYPES.includes(attribute.type);

type AttributeFilterProps = {
  attribute: AttributesEntity;
  /** Selected values of all attributes (the pending filter). */
  selection: CategoryFilterState['attributes'];
  onChange: (update: CategoryFilterUpdate) => void;
  /** Template setting `allow_only_one_value_in_radio_filter`. */
  singleRadio: boolean;
};

/**
 * Values of one category attribute with the core behaviour per type: checkbox/size (and radio unless
 * one value is allowed) toggle values, colours are swatches, select and single radio keep one value,
 * the slider types select every numeric value between the two thumbs.
 */
export function AttributeFilter({
  attribute,
  selection,
  onChange,
  singleRadio,
}: AttributeFilterProps) {
  const values = byPosition(attribute.values).filter(value => value.name);
  const selected = selection.find(item => item.id === attribute.id)?.values ?? [];
  const isChecked = (id: number) => selected.includes(id);
  const toggle = (value: ValuesEntity) => onChange({ selectedValue: { attribute, value } });

  switch (attribute.type) {
    case AttributeType.COLOR:
    case AttributeType.MULTICOLOR: {
      const multicolor = attribute.type === AttributeType.MULTICOLOR;
      return (
        <CheckList
          values={values}
          isChecked={isChecked}
          onToggle={toggle}
          color={value => swatch(value, multicolor)}
        />
      );
    }
    case AttributeType.SELECT:
      return (
        <SelectFilter
          attribute={attribute}
          values={values}
          selected={selected[0]}
          onChange={onChange}
        />
      );
    case AttributeType.SLIDER_RADIO:
    case AttributeType.SLIDER_SELECT: {
      const stops = numericStops(values);
      // A slider needs two numbers to span; otherwise the values stay checkboxes.
      if (stops.length > 1) {
        return (
          <ValueSlider
            attribute={attribute}
            values={values}
            stops={stops}
            selection={selection}
            onChange={onChange}
          />
        );
      }
      break;
    }
    case AttributeType.RADIO:
      if (singleRadio) {
        return (
          <RadioList
            attribute={attribute}
            values={values}
            selected={selected[0]}
            onChange={onChange}
          />
        );
      }
      break;
  }

  return <CheckList values={values} isChecked={isChecked} onToggle={toggle} />;
}

/** Single choice: choosing a value replaces the selection, choosing it again clears it (core). */
function RadioList({
  attribute,
  values,
  selected,
  onChange,
}: {
  attribute: AttributesEntity;
  values: ValuesEntity[];
  selected?: number;
  onChange: (update: CategoryFilterUpdate) => void;
}) {
  return (
    <RadioGroup.Root
      value={selected ? String(selected) : ''}
      aria-label={attribute.name}
      className="flex flex-col gap-3"
    >
      {values.map(value => (
        <label key={value.id} className="flex cursor-pointer items-center gap-3 text-[#0F1012]">
          <RadioGroup.Item
            value={String(value.id)}
            onClick={() => onChange({ selectedValue: { attribute, value }, replace: true })}
            className="flex size-6 shrink-0 items-center justify-center rounded-full border-[1.2px] border-[#9096A0] bg-white transition-colors hover:border-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2 data-[state=checked]:border-[#D7191F]"
          >
            <RadioGroup.Indicator className="size-2.5 rounded-full bg-[#D7191F]" />
          </RadioGroup.Item>
          <span className="dlz-body-m">{value.name}</span>
        </label>
      ))}
    </RadioGroup.Root>
  );
}

function SelectFilter({
  attribute,
  values,
  selected,
  onChange,
}: {
  attribute: AttributesEntity;
  values: ValuesEntity[];
  selected?: number;
  onChange: (update: CategoryFilterUpdate) => void;
}) {
  const { t } = useTranslations();

  return (
    <div className="relative">
      <select
        aria-label={attribute.name}
        value={selected ?? ''}
        onChange={event => {
          const value = values.find(item => item.id === Number(event.target.value));
          onChange(
            value
              ? { selectedValue: { attribute, value }, replace: true }
              : { selectedValue: { attribute, value: values[0] }, remove: true },
          );
        }}
        className="dlz-body-m h-[52px] w-full cursor-pointer appearance-none rounded-[18px] border border-[#E2E4E7] bg-white pl-4 pr-11 text-[#0F1012] transition-[border-color,box-shadow] hover:border-[#9096A0] focus-visible:border-[#0F1012] focus-visible:shadow-[inset_0_0_0_1px_#0F1012] focus-visible:outline-none"
      >
        <option value="">{t('text2498')}</option>
        {values.map(value => (
          <option key={value.id} value={value.id}>
            {value.name}
          </option>
        ))}
      </select>
      <Icon
        name="caret-down"
        size={20}
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7079]"
      />
    </div>
  );
}

/** Distinct numeric value names, ascending – the stops of a slider attribute. */
function numericStops(values: ValuesEntity[]) {
  const numbers = values.map(value => parseNumber(value.name)).filter(n => n !== null);
  return [...new Set(numbers)].sort((a, b) => a - b);
}

/** The slider moves over the attribute's numbers, so every position selects existing values. */
function ValueSlider({
  attribute,
  values,
  stops,
  selection,
  onChange,
}: {
  attribute: AttributesEntity;
  values: ValuesEntity[];
  stops: number[];
  selection: CategoryFilterState['attributes'];
  onChange: (update: CategoryFilterUpdate) => void;
}) {
  const { t } = useTranslations();
  const last = stops.length - 1;
  const stopOf = (value: ValuesEntity) => stops.indexOf(parseNumber(value.name) ?? NaN);
  const selected = selection.find(item => item.id === attribute.id)?.values ?? [];
  const chosen = values
    .filter(value => selected.includes(value.id))
    .map(stopOf)
    .filter(stop => stop >= 0);
  const others = selection.filter(item => item.id !== attribute.id);

  return (
    <RangeFilter
      min={0}
      max={last}
      value={chosen.length ? [Math.min(...chosen), Math.max(...chosen)] : [0, last]}
      labels={[`${attribute.name} ${t('text811')}`, `${attribute.name} ${t('text86')}`]}
      format={stop => formatNumber(stops[stop])}
      parse={text => {
        const typed = parseNumber(text);
        if (typed === null) return null;
        // The nearest stop to the typed number.
        return stops.reduce(
          (best, stop, index) =>
            Math.abs(stop - typed) < Math.abs(stops[best] - typed) ? index : best,
          0,
        );
      }}
      onChange={([from, to]) => {
        const ids = values
          .filter(value => stopOf(value) >= from && stopOf(value) <= to)
          .map(value => value.id);
        // The full range is no constraint – the attribute leaves the filter.
        onChange({
          attributes:
            from === 0 && to === last ? others : [...others, { id: attribute.id, values: ids }],
        });
      }}
    />
  );
}

/** Core colour rule: `colors` or the `color` string split into segments, stacked top to bottom. */
function swatch(value: ValuesEntity, multicolor: boolean) {
  const segments = (value.colors?.length ? value.colors : (value.color ?? '').split(/[\s,;/|]+/))
    .map(segment => segment.trim())
    .filter(Boolean);
  if (!multicolor || segments.length < 2) return value.color ?? undefined;
  const share = 100 / segments.length;
  return `linear-gradient(${segments
    .map((segment, index) => `${segment} ${index * share}% ${(index + 1) * share}%`)
    .join(', ')})`;
}
